import { useLayoutEffect } from 'react';
import axios, { AxiosError } from 'axios';

const FALLBACK_PHONE = '866-936-0602';

function rewriteComplianceTrainingMessage(description: string): string {
    const phoneMatch = description.match(/\b(\d{3}[-.]?\d{3}[-.]?\d{4})\b/);
    const phone = phoneMatch?.[1] ?? FALLBACK_PHONE;
    return `You can still apply by calling ${phone}.`;
}

function isComplianceTrainingError(description: unknown): description is string {
    return (
        typeof description === 'string' && description.toLowerCase().includes('compliance training')
    );
}

/**
 * Axios response-error interceptor that rewrites technician-facing compliance
 * training messages into consumer-friendly copy.
 *
 * This hook should only be used in consumer-facing apps (e.g. homeowner-application,
 * prequals-landing). It will rewrite any error containing "compliance training"
 * into a consumer-friendly message.
 */
export const useAxiosConsumerErrorInterceptor = () => {
    useLayoutEffect(() => {
        const interceptorId = axios.interceptors.response.use(undefined, (error: AxiosError) => {
            const data = error.response?.data as Record<string, unknown> | string | undefined;

            if (typeof data === 'string' && isComplianceTrainingError(data)) {
                (error.response as any).data = rewriteComplianceTrainingMessage(data);
                return Promise.reject(error);
            }

            if (data && typeof data === 'object') {
                if (isComplianceTrainingError((data as any).description)) {
                    (data as any).description = rewriteComplianceTrainingMessage(
                        (data as any).description,
                    );
                }

                if (Array.isArray(data)) {
                    for (const item of data) {
                        if (item && isComplianceTrainingError(item.description)) {
                            item.description = rewriteComplianceTrainingMessage(item.description);
                        }
                    }
                }
            }

            return Promise.reject(error);
        });

        return () => {
            axios.interceptors.response.eject(interceptorId);
        };
    }, []);
};
